import { Injectable, EventEmitter, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { getApps } from 'firebase/app';
import { getMessaging, onMessage, getToken, MessagePayload, Unsubscribe } from 'firebase/messaging';
import { environment } from '../../../environments/environment';
import { ChatService } from '../../components/chat-component/chat-component.service';
import { PushNotification } from '../models/PushNotification.model';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private logger = inject(LoggerService);
  private authService = inject(AuthService);
  private chatService = inject(ChatService);
  private ngZone = inject(NgZone);

  private readonly newPushNotification = new EventEmitter<PushNotification>();
  private readonly recentlyHandledMessageIds = new Set<string>();

  private isListening = false;
  private messageUnsubscribe?: Unsubscribe;

  /**
   * Inizializza service worker, token e listener foreground per gli utenti autenticati
   */
  public async initPushNotifications(): Promise<void> {
    const token = await this.getOrRequestToken();
    if (token) {
      this.startListenForPushNotification();
    }
  }

  /**
   * Registra il service worker firebase-messaging-sw.js e ottiene il token FCM del browser
   */
  public async getOrRequestToken(): Promise<string | null> {
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator) ||
      !('Notification' in window)
    ) {
      this.logger.warn('[FirebaseService] Push notifications non supportate in questo browser.');
      return null;
    }

    try {
      // 1. Registra o recupera il service worker
      let swRegistration: ServiceWorkerRegistration;
      try {
        swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      } catch (swErr) {
        this.logger.warn(
          '[FirebaseService] Impossibile registrare firebase-messaging-sw.js:',
          swErr
        );
        return null;
      }

      // 2. Richiedi permesso notifiche se non ancora deciso
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          this.logger.info('[FirebaseService] Permesso notifiche non concesso:', permission);
          return null;
        }
      } else if (Notification.permission !== 'granted') {
        return null;
      }

      // 3. Attendi che Firebase App sia inizializzata
      if (getApps().length === 0) {
        return null;
      }

      const messaging = getMessaging();
      const currentToken = await getToken(messaging, {
        vapidKey: environment.firebaseConfig.vapidKey,
        serviceWorkerRegistration: swRegistration
      });

      return currentToken || null;
    } catch (err) {
      this.logger.warn('[FirebaseService] Errore durante il recupero del token FCM:', err);
      return null;
    }
  }

  /**
   * Avvia l'ascolto dei messaggi push FCM quando l'applicazione è in FOREGROUND
   */
  public startListenForPushNotification(): void {
    if (this.isListening) {
      return;
    }

    try {
      if (getApps().length === 0) {
        return;
      }

      const messaging = getMessaging();
      this.isListening = true;

      this.messageUnsubscribe = onMessage(messaging, (payload: MessagePayload) => {
        this.ngZone.run(() => {
          this.handleForegroundMessage(payload);
        });
      });
    } catch (err) {
      this.isListening = false;
      this.logger.error('[FirebaseService] FCM listener error', err);
    }
  }

  public stopListening(): void {
    if (this.messageUnsubscribe) {
      this.messageUnsubscribe();
      this.messageUnsubscribe = undefined;
    }
    this.isListening = false;
  }

  public listenForPushNotifications(): EventEmitter<PushNotification> {
    return this.newPushNotification;
  }

  /**
   * Gestione del messaggio ricevuto in foreground
   */
  private handleForegroundMessage(payload: MessagePayload): void {
    // 1. Evita notifiche duplicate per lo stesso evento
    if (this.isDuplicate(payload)) {
      return;
    }

    // 2. Evita notifiche per messaggi inviati dall'utente stesso
    if (this.isSentByCurrentUser(payload)) {
      return;
    }

    const data = payload.data as Record<string, string> | undefined;
    const convId = data?.['conversation_id'] || data?.['chat_id'];
    const msgId =
      payload.messageId || data?.['message_id'] || data?.['client_uuid'] || data?.['id'];

    const senderTitle = data?.['sender_name'] || payload.notification?.title || 'Chat Pazienti';

    this.showNewMessageToast({
      conversationId: convId,
      senderName: senderTitle,
      messageId: msgId
    });

    this.newPushNotification.emit(payload as unknown as PushNotification);
  }

  /**
   * Mostra il toast informativo "Hai un nuovo messaggio" con click per aprire la chat
   * e deduplicazione con TTL di 30 secondi.
   */
  public showNewMessageToast(options: {
    conversationId?: string | number;
    senderName?: string;
    messageId?: string | number;
  }): void {
    const msgId = options.messageId ? String(options.messageId) : null;
    if (msgId && this.recentlyHandledMessageIds.has(msgId)) {
      return;
    }
    if (msgId) {
      this.recentlyHandledMessageIds.add(msgId);
      setTimeout(() => this.recentlyHandledMessageIds.delete(msgId), 30000);
    }

    const convId = options.conversationId ? String(options.conversationId) : undefined;
    const title = options.senderName || 'Chat Pazienti';
    const toast = this.toastr.info('Hai un nuovo messaggio', title, {
      positionClass: 'toast-bottom-left',
      timeOut: 5000,
      tapToDismiss: true,
      closeButton: true
    });

    if (convId) {
      toast.onTap.subscribe(() => {
        this.ngZone.run(() => {
          this.router.navigate(['/dashboard/chat-pazienti'], {
            queryParams: { conversationId: convId }
          });
        });
      });

      // Aggiorna reattivamente il contatore della sidebar se non è la chat attualmente attiva
      if (convId !== this.chatService.activeChatId()) {
        this.chatService.incrementUnreadForConversation(convId);
      }
    }
  }

  /**
   * Verifica se la notifica è un duplicato recente (TTL 30 secondi)
   */
  private isDuplicate(payload: MessagePayload): boolean {
    const data = payload.data as Record<string, string> | undefined;
    const msgId =
      payload.messageId || data?.['message_id'] || data?.['client_uuid'] || data?.['id'];

    if (!msgId) {
      return false;
    }

    if (this.recentlyHandledMessageIds.has(msgId)) {
      return true;
    }

    this.recentlyHandledMessageIds.add(msgId);
    setTimeout(() => this.recentlyHandledMessageIds.delete(msgId), 30000);
    return false;
  }

  /**
   * Verifica se il messaggio è stato originato dall'utente corrente
   */
  private isSentByCurrentUser(payload: MessagePayload): boolean {
    const data = payload.data as Record<string, string> | undefined;
    const senderId = data?.['sender_id'] || data?.['user_id'];
    const currentUserId = this.authService.getUser()?.id || this.chatService.getCurrentUserId();

    if (senderId && currentUserId != null && String(senderId) === String(currentUserId)) {
      return true;
    }
    return false;
  }
}
